<template>
  <div class="accommodations-recap">
    <b-card no-body class="accommodations-recap__wrapper">
      <b-card-body>
        <div
          v-for="item in items"
          :key="item.key"
          class="accommodations-recap__item"
        >
          <span class="accommodations-recap__item-title semibold">{{ item.name }}</span>
          <div class="accommodations-recap__item-content">
            <p class="accommodations-recap__booked">
              Stai prenotando per {{ item.guests }}
            </p>
            <div class="accommodations-recap__matching">
              <b-alert
                v-if="item.match"
                show
                :variant="item.match.variant"
                class="accommodations-recap__badge"
              >
                <vb-icon
                  class="mr-1"
                  :name="item.match.icon"
                  :color="item.match.icon_color"
                  :size="10"
                />
                <span>{{ item.match.text }}</span>
              </b-alert>
              <span v-if="item.unallocated" class="accommodations-recap__unallocated">
                Serve spazio per {{ item.unallocated }}
              </span>
            </div>
            <p
              v-if="item.free_stay_message"
              class="accommodations-recap__note"
            >
              {{ item.free_stay_message }}
            </p>
            <p class="accommodations-recap__note">
              Soluzione pensata per una capienza massima di {{ item.capacity }}
            </p>
            <div class="accommodations-recap__policies">
              <mealplan
                v-if="item.meals.length"
                :meals="item.meals"
              />
              <refundability :cancellation="item.cancellation" />
            </div>
            <p
              v-if="item.overcapacity_notice"
              class="accommodations-recap__overcapacity"
            >
              <vb-icon :name="'exclamation'" :color="sass('danger')" :size="14" />
              <span>{{ item.overcapacity_notice }}</span>
            </p>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
  import Mealplan from '@bookingv3/components/atoms/mealplan'
  import Refundability from '@bookingv3/components/atoms/refundability'
  import {
    format_allocation_overcapacity_notice,
    format_guests,
    format_max_occupancy,
    format_occupancy_mismatch,
    format_product_occupancy,
  } from '@bookingv3/utilities'

  export default {
    name: 'accommodations-recap',

    components: {
      mealplan: Mealplan,
      refundability: Refundability,
    },

    props: {
      solution: {
        type: Object,
        default: () => ({}),
      },
    },

    computed: {
      // Ospiti della ricerca, es. "2 adulti e 2 bambini di 2 e 4 anni". Usato
      // come ripiego quando una camera non ha un'allocation a cui riferirsi.
      booked_guests() {
        const { adults, children } = (this.solution.occupancy || []).reduce((total, item) => {
          total.adults += item.adults
          total.children.push(...(item.children || []))
          return total
        }, { adults: 0, children: [] })

        return format_guests(adults, children)
      },

      /**
       * Una voce per ogni opzione selezionata: i prodotti sono espansi per
       * quantità, quindi la stessa camera scelta più volte genera più voci
       * distinte, ciascuna con il proprio gruppo di ospiti e il proprio esito
       * di capienza.
       *
       * Gruppo ed esito arrivano da `accommodation.products` (fonte API) tramite
       * un pool consumabile: ogni istanza di prodotto prende un'entry distinta
       * correlata per id, così prodotti con lo stesso id (qty > 1) ottengono la
       * propria allocazione invece della stessa ripetuta.
       */
      items() {
        const { accommodation } = this.solution
        const pool = [...((accommodation && accommodation.products) || [])]

        const take = (id) => {
          const index = pool.findIndex((product) => product.id === id)
          return index !== -1 ? pool.splice(index, 1)[0] : null
        }

        const rooms = this.solution.rooms || []
        const items = []

        rooms.forEach((room) => {
          Object.values(room.products).forEach((product) => {
            for (let i = 0; i < product.price_selected.qty; i++) {
              const accommodation_product = take(product.id)
              const occupancy_mismatch = accommodation_product
                ? accommodation_product.occupancy_mismatch
                : null
              const mismatch = format_occupancy_mismatch(occupancy_mismatch)
              const allocation = accommodation_product && accommodation_product.allocation
              
              const room_occupancy = allocation
                ? [{ adults: allocation.number_of_adults, children: allocation.children || [] }]
                : this.solution.occupancy

              const occupancy = format_product_occupancy(
                product.maximum_occupancy,
                room_occupancy
              )

              items.push({
                key: `${room.id_room}-${product.id}-${i}`,
                name: room.name,
                // ospiti del gruppo assegnato a questa camera; senza allocation
                // si ripiega sugli ospiti della ricerca
                guests: allocation
                  ? format_guests(allocation.number_of_adults, allocation.children)
                  : this.booked_guests,
                // senza corrispondenza nei prodotti della preview l'esito non è
                // noto: il badge non viene renderizzato (guardia nel template)
                match: accommodation_product ? this.match_type(occupancy_mismatch) : null,
                unallocated: mismatch ? mismatch.unallocated : '',
                capacity: format_max_occupancy(product.maximum_occupancy),
                free_stay_message: occupancy.free_stay_message,
                overcapacity_notice: format_allocation_overcapacity_notice(
                  allocation,
                  product.maximum_occupancy
                ),
                meals: product.policies.meal_plan.meals,
                cancellation: product.policies.cancellation,
              })
            }
          })
        })

        return items
      },
    },

    methods: {
      // Config del badge in base a occupancy_mismatch (fonte API): assente →
      // camera adatta (success), presente → camera non adatta (danger). Il
      // dettaglio degli ospiti non allocati è mostrato accanto al badge.
      match_type(occupancy_mismatch) {
        if (!occupancy_mismatch) {
          return {
            text: 'Camera adatta',
            icon: 'check_circle',
            icon_color: '0c5028',
            variant: 'success',
          }
        }

        return {
          text: 'Camera non adatta',
          icon: 'exclamation',
          icon_color: '7b151c',
          variant: 'danger',
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  // rosso del testo dell'alert danger, allineato all'icon_color di match_type
  $alert-danger-color: #7b151c;

  .accommodations-recap {
    &__wrapper {
      border-radius: 10px;
      border: none;
    }

    &__booked {
      margin-bottom: 10px;
    }

    &__item {
      border-top: 1px solid $gray-300;
      margin-top: 10px;
      padding-top: 10px;

      &:first-child {
        border-top: 0;
        margin-top: 0;
        padding-top: 0;
      }

      &-title {
        display: block;
        margin-bottom: 5px;
      }

      &-content {
        margin-left: 10px;
      }
    }

    &__matching {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-bottom: 5px;
    }

    &__badge.alert {
      display: flex;
      align-items: center;
      margin-bottom: 0;
      margin-right: 10px;
      padding: 5px;
      font-size: $font-size-md;
      font-weight: $vb-font-weight-semibold;
      line-height: 1;
      border-radius: $border-radius-lg;

      &.alert-danger span {
        color: $alert-danger-color;
      }
    }

    &__unallocated {
      font-size: $font-size-md;
      color: $alert-danger-color;
    }

    &__note {
      font-size: $font-size-md;
      color: $gray-600;
      line-height: normal;
      margin-bottom: 5px;
    }

    &__policies {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-top: 10px;
    }

    &__overcapacity {
      display: flex;
      font-size: $font-size-md;
      color: $danger;
      line-height: normal;
      border-top: 1px solid $gray-200;
      padding-top: 10px;
      margin-top: 10px;
      margin-bottom: 0;

      .vb-icon {
        margin-right: $grid-gutter-width / 4;
        margin-top: $grid-gutter-width / 10;
      }
    }
  }
</style>
